/**
 * Runtime narrowing for values whose type the compiler cannot vouch for: URL
 * and search params, storage, Mantine `onChange` strings, parsed JSON, and loose
 * API fields. Prefer these over `as` so a bad value is caught (or defaulted) at
 * the boundary instead of flowing through the app under a false type.
 */

/** Narrow an untrusted value to a non-null, non-array object before reading its fields. */
export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const isString = (value: unknown): value is string =>
  typeof value === 'string';

export const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

/** `value` when it is a string, otherwise `fallback` (or undefined). */
export function parseString(value: unknown): string | undefined;
export function parseString<F>(value: unknown, fallback: F): string | F;
export function parseString<F>(
  value: unknown,
  fallback?: F
): string | F | undefined {
  return isString(value) ? value : fallback;
}

/**
 * A `responseType: 'blob'` response body as a `Blob`. The generated client
 * types these bodies as `unknown`; anything else throws into the caller's
 * existing error handling.
 */
export function expectBlob(value: unknown, what: string): Blob {
  if (!(value instanceof Blob)) {
    throw new Error(`Expected a file for ${what}`);
  }
  return value;
}

export const isBoolean = (value: unknown): value is boolean =>
  typeof value === 'boolean';

export const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(isString);

/** `guard`, also accepting null. */
export const isNullable = <T>(
  value: unknown,
  guard: (value: unknown) => value is T
): value is T | null => value === null || guard(value);

/** `guard`, also accepting undefined (an absent optional field). */
export const isOptional = <T>(
  value: unknown,
  guard: (value: unknown) => value is T
): value is T | undefined => value === undefined || guard(value);

/** An array whose every element passes `guard`. */
export const isArrayOf = <T>(
  value: unknown,
  guard: (value: unknown) => value is T
): value is T[] => Array.isArray(value) && value.every(guard);

/** `value` when it is a finite number, otherwise `fallback` (or undefined). */
export function parseFiniteNumber(value: unknown): number | undefined;
export function parseFiniteNumber<F>(value: unknown, fallback: F): number | F;
export function parseFiniteNumber<F>(
  value: unknown,
  fallback?: F
): number | F | undefined {
  return isFiniteNumber(value) ? value : fallback;
}

/** `value` when it is a boolean, otherwise undefined. */
export const parseBoolean = (value: unknown): boolean | undefined =>
  isBoolean(value) ? value : undefined;

/** null kept as null; otherwise `parseString`. For nullable wire fields. */
export const parseNullableString = (
  value: unknown
): string | null | undefined => (value === null ? null : parseString(value));

/** null kept as null; otherwise `parseFiniteNumber`. For nullable wire fields. */
export const parseNullableFiniteNumber = (
  value: unknown
): number | null | undefined =>
  value === null ? null : parseFiniteNumber(value);

/**
 * Parse each element of a wire list, dropping (with one warning naming
 * `label`) the elements `parseItem` rejects with null.
 */
export function parseListItems<T>(
  items: readonly unknown[],
  parseItem: (item: unknown) => T | null,
  label: string
): T[] {
  const parsed: T[] = [];
  for (const item of items) {
    const value = parseItem(item);
    if (value !== null) parsed.push(value);
  }
  if (parsed.length < items.length) {
    console.warn(`Dropped ${items.length - parsed.length} malformed ${label}`);
  }
  return parsed;
}

/** Type predicate for `.filter(isDefined)`, dropping null and undefined. */
export const isDefined = <T>(value: T | null | undefined): value is T =>
  value !== null && value !== undefined;

/** Whether `value` is one of `allowed` (compared with `===`). */
export function isOneOf<const T extends string | number>(
  allowed: readonly T[],
  value: unknown
): value is T {
  return (allowed as readonly unknown[]).includes(value);
}

/** `value` when it is one of `allowed`, otherwise `fallback` (or undefined). */
export function parseOneOf<const T extends string | number>(
  allowed: readonly T[],
  value: unknown
): T | undefined;
export function parseOneOf<const T extends string | number>(
  allowed: readonly T[],
  value: unknown,
  fallback: T
): T;
export function parseOneOf<const T extends string | number>(
  allowed: readonly T[],
  value: unknown,
  fallback?: T
): T | undefined {
  return isOneOf(allowed, value) ? value : fallback;
}

// Enum objects are module constants, so each one's value list is built once.
const enumValuesCache = new WeakMap<object, readonly string[]>();

/**
 * The values of a TypeScript string enum or an Orval `as const` enum object.
 * Only string-valued enums are supported: numeric TS enums carry reverse
 * mappings that would leak their keys into the list. The list is cached per
 * enum object, so calling this in render or per row allocates nothing.
 */
export function enumValues<const E extends Record<string, string>>(
  enumObj: E
): readonly E[keyof E][] {
  let values = enumValuesCache.get(enumObj);
  if (values === undefined) {
    values = Object.freeze(Object.values(enumObj));
    enumValuesCache.set(enumObj, values);
  }
  // The cache is keyed by the enum object, so its entry holds E's values.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- values of E are E[keyof E] by construction
  return values as readonly E[keyof E][];
}

export function isEnumValue<const E extends Record<string, string>>(
  enumObj: E,
  value: unknown
): value is E[keyof E] {
  return isOneOf(enumValues(enumObj), value);
}

/** `value` when it is a member of `enumObj`, otherwise `fallback` (or undefined). */
export function parseEnum<const E extends Record<string, string>>(
  enumObj: E,
  value: unknown
): E[keyof E] | undefined;
export function parseEnum<const E extends Record<string, string>>(
  enumObj: E,
  value: unknown,
  fallback: E[keyof E]
): E[keyof E];
export function parseEnum<const E extends Record<string, string>>(
  enumObj: E,
  value: unknown,
  fallback?: E[keyof E]
): E[keyof E] | undefined {
  return isEnumValue(enumObj, value) ? value : fallback;
}

/**
 * A runtime list for a type-only string union. Taking `Record<T, true>` makes a
 * missing or extra member a compile error, so the list cannot drift from `T`.
 */
export function literalValues<T extends string>(
  members: Record<T, true>
): readonly T[] {
  // Object.keys widens to string[]; the keys of Record<T, true> are exactly T.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- keys of Record<T, true> are T by construction
  return Object.keys(members) as T[];
}

/**
 * Adapt a typed handler to Mantine's `(value: string | null) => void` onChange
 * (Select, SegmentedControl, Radio.Group, Tabs). A value outside `allowed`, or
 * null, is logged and ignored rather than passed through under a false type.
 */
export function onOneOf<const T extends string>(
  allowed: readonly T[],
  handler: (value: T) => void
): (value: string | null) => void {
  return (value) => {
    if (isOneOf(allowed, value)) {
      handler(value);
      return;
    }
    console.warn('Ignoring unexpected option value', value);
  };
}

/** Like {@link onOneOf}, but a cleared (null) value is passed through as null. */
export function onOneOfOrNull<const T extends string>(
  allowed: readonly T[],
  handler: (value: T | null) => void
): (value: string | null) => void {
  return (value) => {
    if (value === null || isOneOf(allowed, value)) {
      handler(value);
      return;
    }
    console.warn('Ignoring unexpected option value', value);
  };
}

/**
 * `map[key]` when `key` is the map's own property, otherwise undefined. Use for
 * label/color lookup tables indexed by a loose runtime string, so a value like
 * `constructor` falls through to the caller's fallback instead of returning an
 * Object.prototype member.
 */
export function lookupOwn<V>(
  map: Readonly<Record<string, V>>,
  key: string | null | undefined
): V | undefined {
  if (key === null || key === undefined) return undefined;
  return hasOwnKey(map, key) ? map[key] : undefined;
}

/**
 * Own-property check that narrows `key` for indexing a lookup map. Unlike `in`,
 * it ignores prototype members, so a key like `constructor` is not a hit.
 */
export function hasOwnKey<O extends object>(
  obj: O,
  key: PropertyKey
): key is keyof O {
  return Object.prototype.hasOwnProperty.call(obj, key);
}
